import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Users, MapPin, Phone, AlertTriangle, CheckCircle } from 'lucide-react';
import { t } from '../contexts/translations';

const Safety = ({ language = 'en' }) => {
  const safetyMeasures = [
    {
      icon: <Shield className="w-8 h-8 text-blue-600" />,
      title: t('verifiedPartners', language),
      description: t('verifiedPartnersDesc', language)
    },
    {
      icon: <Users className="w-8 h-8 text-green-600" />,
      title: t('professionalGuides', language),
      description: t('professionalGuidesDesc', language)
    },
    {
      icon: <MapPin className="w-8 h-8 text-purple-600" />,
      title: t('safeLocations', language),
      description: t('safeLocationsDesc', language)
    },
    {
      icon: <Phone className="w-8 h-8 text-red-600" />,
      title: t('support247', language),
      description: t('support247Desc', language)
    }
  ];

  const travelTips = language === 'fr' ? [
    "Gardez des copies de vos documents importants (passeport, carte d'identité, assurance voyage) dans des endroits séparés",
    "Partagez votre itinéraire avec votre famille ou vos amis restés au pays",
    "Restez hydraté et protégez-vous du soleil, surtout dans les zones désertiques",
    "Respectez les coutumes locales et les codes vestimentaires, en particulier lors de la visite de lieux religieux",
    "Utilisez les services de transport officiels et évitez les opérateurs non agréés",
    "Gardez les numéros d'urgence enregistrés dans votre téléphone",
    "Souscrivez une assurance voyage complète avant votre voyage",
    "Soyez conscient de votre environnement et faites confiance à votre instinct"
  ] : [
    "Keep copies of important documents (passport, ID, travel insurance) in separate locations",
    "Share your itinerary with family or friends back home",
    "Stay hydrated and protect yourself from the sun, especially in desert areas",
    "Respect local customs and dress codes, particularly when visiting religious sites",
    "Use official transportation services and avoid unlicensed operators",
    "Keep emergency contact numbers saved in your phone",
    "Purchase comprehensive travel insurance before your trip",
    "Be aware of your surroundings and trust your instincts"
  ];

  const emergencyContacts = [
    { service: language === 'fr' ? "Police" : "Police", number: "19" },
    { service: language === 'fr' ? "Urgences Médicales" : "Medical Emergency", number: "15" },
    { service: language === 'fr' ? "Pompiers" : "Fire Department", number: "15" },
    { service: language === 'fr' ? "Police Touristique" : "Tourist Police", number: "05 24 38 46 01 or 05 24 88 86 80" },
    { service: language === 'fr' ? "Gendarmerie" : "Gendarmerie", number: "077" }
  ];

  return (
    <div className="py-24 min-h-screen relative overflow-hidden" style={{background: 'linear-gradient(to bottom, #fff7ed, #f9e9d2 80%)'}}>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]"></div>
      <div className="container mx-auto px-6 max-w-7xl relative z-10" style={{ marginTop: '3rem' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-20"
        >
          <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
            {t('safetyFirst', language)}
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-6 tracking-tight text-amber-900">{t('safetyPageTitle', language)}</h1>
          <p className="text-lg max-w-3xl mx-auto leading-relaxed text-amber-900/80">
            {t('safetyPageSubtitle', language)}
          </p>
        </motion.div>

        {/* Safety Measures */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="mb-20"
        >
          <h2 className="text-3xl font-semibold text-center mb-12 text-orange-600">{t('safetyMeasures', language)}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {safetyMeasures.map((measure, index) => (
              <div key={index} className="bg-white/90 border border-amber-100 rounded-3xl p-8 text-center shadow-xl hover:shadow-2xl transition-all duration-300">
                <div className="flex justify-center mb-6">
                  <div className="p-4 rounded-xl bg-orange-100">
                    {measure.icon}
                  </div>
                </div>
                <h3 className="text-lg font-semibold mb-4 text-amber-900">{measure.title}</h3>
                <p className="text-amber-900/80">{measure.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20"
        >
          {/* Travel Safety Tips */}
          <div className="bg-white/90 border border-amber-100 rounded-3xl p-8 shadow-xl">
            <h2 className="text-2xl font-semibold mb-8 flex items-center text-orange-600">
              <div className="p-2 rounded-xl bg-green-100 mr-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              {t('travelTips', language)}
            </h2>
            <ul className="space-y-4">
              {travelTips.map((tip, index) => (
                <li key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  <span className="text-amber-900/80">{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Emergency Contacts */}
          <div className="bg-white/90 border border-amber-100 rounded-3xl p-8 shadow-xl">
            <h2 className="text-2xl font-semibold mb-8 flex items-center text-orange-600">
              <div className="p-2 rounded-xl bg-red-100 mr-4">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              {t('emergencyContacts', language)}
            </h2>
            <div className="space-y-4">
              {emergencyContacts.map((contact, index) => (
                <div key={index} className="flex justify-between items-center py-3 border-b border-amber-100 last:border-b-0">
                  <span className="text-amber-900">{contact.service}</span>
                  <span className="font-semibold text-amber-900">{contact.number}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 p-6 bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-2xl">
              <div className="flex items-center">
                <div className="p-2 rounded-xl bg-red-100 mr-3">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <span className="text-red-800 font-semibold">Important</span>
              </div>
              <p className="text-red-700 mt-2">
                {t('emergencyContactsDesc', language)}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Health & Medical */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="bg-white/90 border border-amber-100 rounded-3xl p-8 mb-12 shadow-xl"
        >
          <h2 className="text-2xl font-semibold mb-8 text-orange-600 text-center">{t('healthInfo', language)}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold mb-6 text-amber-900">{t('beforeYouTravel', language)}</h3>
              <ul className="space-y-3 text-amber-900/80">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Consultez votre médecin pour les vaccinations recommandées' : 'Consult your doctor about recommended vaccinations'}
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Apportez les médicaments nécessaires dans leurs contenants d\'origine' : 'Bring necessary medications in original containers'}
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Envisagez une assurance voyage avec couverture médicale' : 'Consider travel insurance with medical coverage'}
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-6 text-amber-900">{t('duringYourStay', language)}</h3>
              <ul className="space-y-3 text-amber-900/80">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Buvez de l\'eau en bouteille ou filtrée' : 'Drink bottled or properly filtered water'}
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Utilisez un écran solaire et restez hydraté' : 'Use sunscreen and stay hydrated in hot weather'}
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                  {language === 'fr' ? 'Consultez un médecin si vous ne vous sentez pas bien' : 'Seek medical attention if you feel unwell'}
                </li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Safety;